import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@components/ui/tooltip';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { FilterOperatorSelect } from './FilterOperatorSelect';
import type { FilterOperator } from './types';

type FilterPillProps = {
  label: string;
  icon: ReactNode;
  operator: FilterOperator;
  onOperatorChange: (operator: FilterOperator) => void;
  onRemove: () => void;
  /** The popover trigger + content for editing this filter's values */
  children: ReactNode;
};

export const FilterPill = ({
  label,
  icon,
  operator,
  onOperatorChange,
  onRemove,
  children
}: FilterPillProps) => (
  <div className="flex h-7 items-stretch rounded-md border border-border border-solid bg-card text-xs shadow-sm">
    {/* Label section */}
    <div className="flex items-center gap-1.5 px-2 text-muted-foreground">
      {icon}
      <span>{label}</span>
    </div>

    {/* Operator select */}
    <div className="flex border-border border-l">
      <FilterOperatorSelect onChange={onOperatorChange} value={operator} />
    </div>

    {/* Value section (wraps the popover trigger) */}
    <div className="flex border-border border-l">{children}</div>

    {/* Remove button */}
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            aria-label={`Remove ${label} filter`}
            className="flex items-center border-border border-l px-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            onClick={e => {
              e.stopPropagation();
              onRemove();
            }}
            type="button"
          >
            <X className="size-3" />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <span className="text-xs">Remove filter</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  </div>
);
